<template>
  <ModalComponent ref="modal" :activated="activated" modal-style="height: 100%;" @close="$emit('close')">
    <template #header>
      <h3>Exportera</h3>
    </template>

    <ToggleComponent v-model="typeOfExport" tabs :options="exportOptions" />

    <!----------------->
    <!-- Data export -->
    <!----------------->
    <template v-if="typeOfExport === 'productData'">
      <!-- Language/Country -->
      <div class="mb-2 form-inline">
        Översätt om möjligt värden till

        <select v-model="output.translationLanguage" class="form-control m-2">
          <option v-for="lang in store.availableLanguages" :key="lang" :value="lang">
            {{ translate(lang, 'languages', undefined, 'sv') }}
          </option>
        </select>

        med rubriker på
        <select v-model="output.headerLanguage" class="form-control m-2">
          <option v-for="lang in store.availableLanguages" :key="lang" :value="lang">
            {{ translate(lang, 'languages', undefined, 'sv') }}
          </option>
        </select>
      </div>

      <p>
        Fält för <em>{{ translate('name', 'attributes') }}, {{ translate('description', 'attributes') }}, USPar, priser</em> (utan kulturellt prefix)
        <template v-if="!store.dataCulture"> kommer inte kunna exporteras eftersom du inte har en vald primär datakultur.</template>
        <template v-else>
          exporteras enligt din valda primära datakultur:
          <strong> {{ translate(store.dataCulture?.language, 'languages') }}-{{ translate(store.dataCulture?.country, 'countries', 'capitalize') }}</strong>
          <img class="small-flag ml-1" :src="getFlag(store.dataCulture?.country)" />.
        </template>
        För att exportera specifik kulturell data, välj alternativet nedan "visa specifika kulturella fält" och inkludera fälten med lands- och språkprefix.
      </p>

      <label class="user-select-none">
        <input v-model="showSpecificCulturalFields" type="checkbox" />
        Visa specifika kulturella fält
      </label>
      <InfoPopup class="ml-1 position-relative">
        Tillgängliggör export av kolumner för priser, benämning, beskrivningar, USPar för samtliga länder/språk
      </InfoPopup>

      <input
        v-model="searchTerm"
        type="search"
        class="form-control"
        placeholder="Sök..."
        @keydown.enter="focusList(availablePropertiesList)"
        @keydown.down.prevent="focusList(availablePropertiesList)"
        @keydown.esc="searchTerm ? $event.stopPropagation() : 0"
      />

      <div class="row mt-3">
        <!-- Available fields -->
        <div class="col">
          <h5>Tillgängliga kolumner</h5>
          <small v-if="searchTerm">
            <em>Visar filtrerat resultat. {{ nrOfHiddenProperties }} kolumner har dolts</em>
          </small>
          <select
            ref="availablePropertiesList"
            multiple
            class="minimalistic list"
            @keydown.right="focusList(includedPropertiesList)"
            @keydown.insert="addProperties(getSelectedAvailableItems())"
            @keydown="handleSearchFromList"
            @mousedown="preventScrollOnFocus"
          >
            <option
              v-for="(property, i) of filteredAvailableProperties"
              :key="i"
              :class="{ 'mt-3': property.order > 0 && property.order !== filteredAvailableProperties[i - 1]?.order }"
              :value="i"
              :hidden="property.hidden"
              @dblclick="addProperties([property])"
            >
              {{ property.header }}
            </option>
          </select>

          <small> Tangentbordsnavigering: navigering med piltangenter, lägg till med <em>Insert</em> </small>
        </div>

        <!-- Buttons -->
        <div class="col-1 col-md-2 buttons p-0">
          <!-- Add / Delete / Clear -->
          <div class="col p-0 pr-md-1">
            <button class="btn btn-primary" :disabled="!filteredAvailableProperties.length" @click="addProperties(getSelectedAvailableItems())">
              <!-- :disabled="!getSelectedAvailableItems().length" -->
              <span class="d-md-inline-block d-none">Lägg till</span>
              &raquo;
            </button>

            <button class="btn btn-warning" :disabled="!includedProperties.length" @click="removeProperties(getSelectedIncludedItems())">
              <!-- :disabled="!getSelectedIncludedItems().length" -->
              &laquo;
              <span class="d-md-inline-block d-none">Ta bort</span>
            </button>

            <button type="button" class="btn btn-danger mt-3" :disabled="!includedProperties.length" @click="resetMappings()">
              <span class="d-block d-md-none"> &times; </span>
              <span class="d-md-block d-none">Rensa</span>
            </button>
          </div>

          <!-- Sorting -->
          <div class="col col-md-4 col-lg-3 p-0 pl-md-1">
            <fieldset :disabled="!includedProperties.length">
              <button class="btn btn-secondary" @click="orderIncludedProperties(-1)">
                <i class="fa fa-level-up" />
              </button>
              <button class="btn btn-secondary" @click="orderIncludedProperties(1)">
                <i class="fa fa-level-down" />
              </button>
            </fieldset>
          </div>
        </div>

        <!-- Included fields -->
        <div class="col">
          <h5>Inkluderade kolumner</h5>
          <select
            ref="includedPropertiesList"
            multiple
            class="minimalistic list"
            @keydown.exact.left="focusList(availablePropertiesList)"
            @keydown.exact.delete="$event.key === 'Delete' ? removeProperties(getSelectedIncludedItems()) : 0"
            @keydown.alt.up.prevent="orderIncludedProperties(-1)"
            @keydown.alt.down.prevent="orderIncludedProperties(1)"
            @keydown.shift="handleSearchFromList"
            @keydown.ctrl="handleSearchFromList"
            @keydown.exact="handleSearchFromList"
            @mousedown="preventScrollOnFocus"
          >
            <option
              v-for="(property, i) of includedProperties"
              :key="i"
              :value="property.defaultHeader"
              :class="{ disabled: !matchesSearchTerm(property.header) && !matchesSearchTerm(String(property.defaultName)) }"
              @dblclick="removeProperties([property.defaultHeader])"
            >
              <!-- :disabled="!matchesSearchTerm(property.name) && !matchesSearchTerm(property.defaultName)" -->
              {{ property.header }}
            </option>
          </select>

          <div>
            <label for="addPositions" class="mr-2">Lägg till nya kolumner</label>
            <select id="addPositions" v-model="addPosition">
              <option v-for="position of addPositions" :key="position" :value="position">
                {{ translate(position, 'application', undefined, store.translationLanguage) }}
              </option>
            </select>
          </div>

          <small> Tangentbordsnavigering: navigering med piltangenter, ordna med <em>alt</em> + pil upp/ner, ta bort med <em>Delete</em> </small>
        </div>
      </div>

      <hr />

      <label>
        <input v-model="store.useNumericValuesForExport" type="checkbox" />
        Exkludera nollvärde-decimaler för dimensioner och priser
      </label>

      <div class="row">
        <!-- Load template -->
        <div class="col">
          <form @submit.prevent="loadSelectedTemplate(stagedLoadTemplate)">
            <label for="template">Ladda från mall</label>
            <div class="form-group form-inline">
              <select v-model="stagedLoadTemplate" class="form-control small d-inline-block mb-2 mr-2" name="template">
                <option hidden value="">-- Välj en mall --</option>
                <optgroup
                  v-for="(group, templateVisibility, i) in availableTemplates"
                  :key="templateVisibility"
                  :label="appTranslate(`${templateVisibility} templates`)"
                >
                  <option v-for="template in group" :key="template.name" :value="{ visibility: templateVisibility, name: template.name, id: template.id }">
                    {{ template.name }}
                  </option>
                  <!-- spacer -->
                  <option v-if="i < Object.keys(availableTemplates)?.length - 1" disabled />
                </optgroup>
              </select>
              <div v-if="stagedLoadTemplate?.id" class="mb-2">
                <button type="submit" class="btn btn-warning">Ladda</button>
                <button
                  v-if="store.userPrivileges.has('export') || stagedLoadTemplate.visibility === 'private'"
                  type="button"
                  class="btn btn-danger ml-2"
                  @click="removeSelectedTemplate()"
                >
                  Ta bort
                </button>
              </div>
            </div>
          </form>
        </div>

        <!-- Save template -->
        <div class="col">
          <form v-show="includedProperties.length" class="ml-auto" @submit.prevent="saveTemplate(stagedSaveTemplate)">
            <div>Spara som mall</div>
            <div class="form-group form-inline">
              <input
                v-model="stagedSaveTemplate"
                placeholder="Skriv in ett namn..."
                type="text"
                list="availableTemplates"
                class="form-control mr-2 mb-2"
                style="width: 300px"
              />
              <datalist id="availableTemplates">
                <option v-for="template in store.templates.export[visibility]" :key="template.name">{{ template.name }}</option>
              </datalist>

              <button type="submit" class="btn btn-primary mb-2">Spara</button>
            </div>

            <label class="d-block">
              Synlighet:
              <select v-model="visibility" :disabled="!store.userPrivileges.has('export')">
                <option value="public">{{ translate('public', 'application') }}</option>
                <option value="private">{{ translate('private', 'application') }}</option>
              </select>
              <!-- 
                <BooleanToggle falseValue="public" trueValue="private" v-model="visibility" />
              <input type="checkbox" v-model="visibility" :true-value="'public'" :false-value="'private'" />
              {{ translate(visibility, 'application') }}
              -->
            </label>
          </form>
        </div>
      </div>

      <div>
        <button v-if="store.isDevEnvironment" class="btn btn-primary dev" @click="loadPropertiesSelectedInTableView()">Ladda valda fält från tabellvyn</button>
      </div>
    </template>

    <!----------------------->
    <!-- Export EAN labels -->
    <!----------------------->
    <div v-if="typeOfExport === 'eanLabels'" class="mt-2">
      <PrintWarnings class="mb-4" />

      <fieldset :disabled="false" class="d-flex flex-wrap">
        <label>
          Typ av etikett
          <select v-model="eanLabelType" class="form-control" style="width: fit-content">
            <option v-for="labelType of EanLabelTypes" :key="labelType" :value="labelType">
              {{ translate(labelType, 'application', 'capitalize', 'sv') }} ({{ getEanLabelDimensions(labelType) }})
            </option>
          </select>
        </label>

        <label class="ml-4">
          Datakultur
          <i class="link fa" :class="eanLabelCultureLocked ? 'fa-lock' : 'fa-unlock'" title="Lås upp datakultur" @click="toggleEanLabelCulture()" />

          <select
            v-model="eanLabelCulture"
            class="form-control"
            style="width: fit-content"
            :disabled="eanLabelCultureLocked"
            :title="eanLabelCultureLocked ? 'Datakulturen är låst till din valda kultur. Du kan låsa upp genom att klicka på hänglåset' : ''"
          >
            <option v-for="labelCulture of store.availableCultures" :key="getLocaleString(labelCulture)" :value="labelCulture">
              {{ getLocaleString(labelCulture) }}
            </option>
          </select>
        </label>
      </fieldset>
    </div>

    <!--------------------->
    <!-- Printed Summary -->
    <!--------------------->
    <div v-else-if="typeOfExport === 'productSheet'" class="mt-2">
      <span> Produktdata enligt vald datakultur </span>
      <img class="small-flag mr-1" :src="getFlag(store.dataCulture?.country)" />
      <em v-if="store.dataCulture"> {{ getLocaleString(store.dataCulture) }} </em>

      <ProductSheetComponent v-if="activated" :key="$route.fullPath" preview class="vertically-faded" />
      <p v-if="total > 3" class="position-relative font-italic m-4">... ytterligare {{ total - 3 }} artiklar</p>
    </div>

    <!------------------------------->
    <!-- Footer - Export button(s) -->
    <!------------------------------->

    <!-- Data export action buttons -->
    <template v-if="typeOfExport === 'productData'" #footer>
      <label class="w-100 font-weight-bold">
        Filnamn
        <input v-model="fileName" type="search" class="form-control mb-2" :placeholder="defaultFileName" @keydown.esc="handleEscape($event)" />
      </label>

      <ProgressComponent
        v-for="(progress, i) of store.activeProgresses.productsExport"
        :key="i"
        class="mb-2"
        :progress="progress"
        bar-style="transition: width .1s"
      />

      <fieldset :disabled="!includedProperties.length">
        <ActionPendingButton
          class="position-relative"
          :disabled="!!exporting.productData.total"
          :pending="!!exporting.productData.total"
          @click="exportProducts()"
        >
          <i class="fa fa-file-excel-o mr-1" />
          Exportera ({{ exporting.productData.total || total }} artiklar)
        </ActionPendingButton>
        <ActionPendingButton
          v-if="store.isDevEnvironment"
          class="ml-2 dev"
          variant="warning"
          :disabled="!!exporting.productData.total"
          @click="exportProducts(true)"
        >
          Print export
        </ActionPendingButton>

        <button v-if="store.activeProgresses.productsExport?.length" class="btn btn-danger ml-2" @click="abortProductsExport()">Avbryt</button>
      </fieldset>
    </template>

    <!-- EAN action buttons -->

    <template v-else-if="typeOfExport === 'eanLabels'" #footer>
      <div v-if="eanLabelPreview && eanLabelPreview?.included.length !== (exporting.eanLabels.total || total)" class="mb-2">
        <TooltipComponent direction="up">
          <template #trigger> Etiketter för {{ eanLabelPreview?.excluded.length }} artiklar kommer inte kunna exporteras </template>

          <p>{{ eanLabelPreview?.excluded.join(', ') }}</p>
          <a :href="`/products?${getSkuFilterString(eanLabelPreview.excluded)}`" target="external"> Visa artiklar <i class="fa fa-external-link" /> </a>
        </TooltipComponent>
        <small :title="eanLabelPreview?.excluded.join(', ')"> </small>
      </div>

      <ActionPendingButton
        :disabled="!!exporting.eanLabels.total"
        :pending="!!exporting.eanLabels.total || !eanLabelPreview"
        type="button"
        @click="exportEanLabels()"
      >
        <i class="fa fa-file-pdf-o mr-1" />
        <template v-if="!eanLabelPreview?.included"> Laddar... </template>
        <template v-else> Exportera ({{ exporting.eanLabels.total ?? eanLabelPreview?.included.length }} etiketter) </template>
      </ActionPendingButton>

      <button v-if="!!exporting.eanLabels.total || store.activeProgresses?.eanLabelExport?.length" class="btn btn-danger ml-2" @click="abortEanLabelExport()">
        Avbryt
      </button>

      <a v-if="!!exporting.eanLabels.link" class="ml-2" :href="exporting.eanLabels.link" target="_new"> Visa artiklar <i class="fa fa-external-link" /> </a>

      <ProgressComponent v-for="(progress, i) of store.activeProgresses.eanLabelExport" :key="i" :progress="progress" />
    </template>

    <!-- Product sheet action buttons -->
    <template v-else-if="typeOfExport === 'productSheet'" #footer>
      <ActionPendingButton
        v-if="total && store.userPrivileges.has('export')"
        :disabled="exportProductSheet"
        :pending="exportProductSheet"
        variant="outline-primary"
        class="text-nowrap m-1"
        @click="triggerExportProductSheet()"
      >
        <i class="fa fa-print mr-1" />
        Skriv ut {{ exporting.productSheet.total || total }} artiklar (PDF)
      </ActionPendingButton>
    </template>
  </ModalComponent>
</template>

<script lang="ts" setup>
import ActionPendingButton from '@/components/ActionPendingButton.vue'
import PrintWarnings from '@/components/export/PrintWarnings.vue'
import ProductSheetComponent from '@/components/export/ProductSheetComponent.vue'
import ModalComponent from '@/components/ModalComponent.vue'
import ProgressComponent from '@/components/ProgressComponent.vue'
import InfoPopup from '@/components/simple/InfoPopupComponent.vue'
import TooltipComponent from '@/components/simple/TooltipComponent.vue'
import ToggleComponent from '@/components/ToggleComponent.vue'
import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import { EanLabelType, EanLabelTypes } from '@/models/Ean'
import { ExportType, ExportTypes, ProductExportTemplateInput } from '@/models/Export'
import { Culture, Language, Option } from '@/models/Global'
import { AdvancedProperty, PartialProperty } from '@/models/Property'
import { TemplateVisibility } from '@/models/Templates'
import DataService from '@/services/DataService'
import ExportService from '@/services/ExportService'
import PropertyService from '@/services/PropertyService'
import { useDefaultStore } from '@/store'
import { browserStorage } from '@/utils/browserStorage'
import { getEanLabelDimensions } from '@/utils/productUtils'
import { addToArray, areComparable, arrayToMap, camelCaseToTerm, getLocaleString, getSkuFilterString, moveArrayItem, toComparable } from '@/utils/tools'
import { ComponentPublicInstance, computed, defineEmits, defineProps, onMounted, ref, watch, watchEffect, withDefaults } from 'vue'
import { useRoute } from 'vue-router'

// -----------
// -- Setup --
// -----------
const addPositions = ['first', 'last', 'before selection', 'after selection'] as const
const invalidCountries = ['dk', 'no']
// const validCountries = ['se', 'fi']

type ExportAdvancedProperty = AdvancedProperty & {
  order: number
  hidden?: boolean
}

const props = withDefaults(
  defineProps<{
    activated?: boolean
    total: number
    exportProductSheet?: boolean
  }>(),
  { activated: true, default: 0 },
)

const emit = defineEmits(['update:exportProductSheet', 'close'])

const { translate, appTranslate } = useTranslation()
const { getFlag } = useTools()
const store = useDefaultStore()
const route = useRoute()

const output = {
  headerLanguage: store.translationLanguage as Language,
  translationLanguage: store.translationLanguage as Language,
}

// -------------------
// -- Reactive vars --
// -------------------
const initiated = ref(false)
const availableProperties = ref<ExportAdvancedProperty[]>([])
const includedProperties = ref<ExportAdvancedProperty[]>([])
const searchTerm = ref('')
const addPosition = ref<(typeof addPositions)[number]>('last')
const visibility = ref<TemplateVisibility>('private')
const stagedSaveTemplate = ref('')
const stagedLoadTemplate = ref<{ visibility: TemplateVisibility; id: string; name: string } | undefined>()
const fileName = ref('')
const showSpecificCulturalFields = ref(false)
const typeOfExport = ref<ExportType>('productData')
const eanLabelType = ref<EanLabelType>(EanLabelTypes[0])
const eanLabelCultureLocked = ref(true)
const exporting = ref<Record<ExportType, { total?: number; link?: string }>>({
  productData: {},
  eanLabels: {},
  productSheet: {},
})

const exportOptions = computed((): Option[] => {
  return ExportTypes.map((type) => ({
    value: type,
    displayValue: appTranslate(camelCaseToTerm(type)),
    experimental: type === 'eanLabels',
  }))
})

const defaultFileName = ref<string>('')

const availablePropertiesList = ref<HTMLSelectElement>()
const includedPropertiesList = ref<HTMLSelectElement>()
const modal = ref<ComponentPublicInstance>()

const eanLabelCulture = ref<Culture>()
const eanLabelPreview = ref<{ included: string[]; excluded: string[] }>()

// --------------
// -- Computed --
// --------------
const filteredAvailableProperties = computed(() => {
  const searchTermMatches = (value: string | number | undefined) => toComparable(value).includes(toComparable(searchTerm.value))

  return availableProperties.value.filter((property) => {
    if (property.hidden) {
      return false
    }

    const searchMatches = searchTermMatches(property.header) || searchTermMatches(property.defaultHeader)
    const matchesCultureSetting = showSpecificCulturalFields.value
      ? !!property.culturePrefix || (property.type !== 'countrySpecific' && property.type !== 'languageSpecific')
      : !property.culturePrefix
    return searchMatches && !property.hidden && matchesCultureSetting
  })
})

const nrOfHiddenProperties = computed(() => {
  const nrOfAvailableProperties = availableProperties.value.reduce((sum, item) => {
    const matchesCultureSetting = showSpecificCulturalFields.value
      ? !!item.culturePrefix || (item.type !== 'countrySpecific' && item.type !== 'languageSpecific')
      : !item.culturePrefix
    return (sum += matchesCultureSetting ? 1 : 0)
  }, 0)

  // length of original availableProperties list
  return nrOfAvailableProperties - filteredAvailableProperties.value.length
})

const availableTemplates = computed(() => {
  const templates = Object.entries(store.templates.export)
    .sort(([a], [b]) => a.localeCompare(b))
    .filter(([, group]) => group?.length)
  return Object.fromEntries(templates)
})

const availablePropertiesById = computed(() => arrayToMap(availableProperties.value, 'id'))
const availablePropertiesByComparableHeader = computed(() => arrayToMap(availableProperties.value, 'defaultHeader', toComparable))

// -----------------------------
// -- Watch & Lifecycle hooks --
// -----------------------------

watch(
  () => props.activated,
  () => {
    if (!initiated.value) {
      initiate()
    }

    defaultFileName.value = `burde-pim-export-${new Date()?.toISOString().substring(0, 10)}`
  },
)

watch(stagedLoadTemplate, (loadTemplate) => {
  if (loadTemplate) {
    visibility.value = store.userPrivileges.has('export') ? loadTemplate.visibility : 'private'
  }
})

watch(
  () => props.exportProductSheet,
  (value) => {
    if (value) {
      exporting.value.productSheet = {
        total: props.total,
        link: route.fullPath,
      }
    } else {
      exporting.value.productSheet = {}
    }
  },
)

watch(
  () => store.translationLanguage,
  () => {
    availableProperties.value = getPropertiesWithTranslatedHeaders(availableProperties.value)
    includedProperties.value = getPropertiesWithTranslatedHeaders(includedProperties.value)
  },
)

watchEffect(() => {
  eanLabelCulture.value =
    store.dataCulture && !invalidCountries.includes(store.dataCulture?.country) ? store.dataCulture : { language: 'sv' as const, country: 'se' as const }
})

watchEffect(async () => {
  if (!props.activated) {
    eanLabelPreview.value = undefined
  } else if (typeOfExport.value === 'eanLabels') {
    eanLabelPreview.value = undefined
    const culture = eanLabelCulture.value ?? store.dataCulture
    eanLabelPreview.value = culture ? await ExportService.getEanLabelExportPreview(new URLSearchParams(location.search), culture) : undefined
  }
})

onMounted(() => {
  if (props.activated && !initiated.value) {
    initiate()
  }
})

// -------------
// -- Methods --
// -------------
const initiate = async () => {
  DataService.loadTemplates('export', 'public')
  DataService.loadTemplates('export', 'private')
  availableProperties.value = await getProperties()
  initiated.value = true
}

const getProperties = async () => {
  const availableProperties = await PropertyService.getPropertyGroups('export')
  return availableProperties.flatMap((group, i) => group.map((prop) => ({ ...prop, order: i })))
}

// const getSelectedAvailableItems = () => Array.from(availablePropertiesList.value?.selectedOptions || []).map((item) => ({ defaultHeader: item.value }))
const getSelectedAvailableItems = () =>
  Array.from(availablePropertiesList.value?.selectedOptions || []).map((option) => filteredAvailableProperties.value[Number(option.value)])

const getSelectedIncludedItems = () =>
  Array.from(includedPropertiesList.value?.selectedOptions || [])
    .filter((item) => !item.disabled)
    .map((item) => item.value)

const getPropertiesWithTranslatedHeaders = (properties: ExportAdvancedProperty[], translationLanguage?: Language) =>
  properties.map((property) => {
    return { ...property, header: PropertyService.getTranslatedHeader(property, translationLanguage || store.translationLanguage) }
  })

const addProperties = (partialProperty: PartialProperty[]) => {
  const firstSelectedIndex = includedPropertiesList.value?.selectedOptions.item(0)?.index
  const lastSelectedIndex = includedPropertiesList.value?.selectedOptions.item(includedPropertiesList.value?.selectedOptions.length - 1)?.index

  let addToIndex =
    addPosition.value === 'before selection'
      ? (firstSelectedIndex ?? 0)
      : addPosition.value === 'after selection'
        ? lastSelectedIndex !== undefined
          ? lastSelectedIndex + 1
          : includedProperties.value.length
        : addPosition.value === 'first'
          ? 0
          : includedProperties.value.length

  partialProperty.forEach((partialProperty) => {
    const foundProp = partialProperty.id
      ? availablePropertiesById.value?.get(partialProperty.id)
      : availablePropertiesByComparableHeader.value?.get(toComparable(PropertyService.getHeader(partialProperty.defaultName, partialProperty.culturePrefix)))

    if (foundProp) {
      foundProp.hidden = true
      addToArray(includedProperties.value, foundProp, addToIndex)
      addToIndex++
    } else {
      // addToArray(includedProperties.value, {}, addToIndex)
      // addToIndex++
      console.log('could not find prop matching', partialProperty)
    }
  })

  // fix expected list selection behaviour
  if (availablePropertiesList.value) {
    availablePropertiesList.value.selectedIndex = Math.min(availablePropertiesList.value.selectedIndex, filteredAvailableProperties.value.length - 1) // select last available item in the list when adding the last item
  }

  if (includedPropertiesList.value && (addPosition.value === 'first' || addPosition.value === 'before selection')) {
    // keep the previously selected item selected when item(s) is added before the selected item
    includedPropertiesList.value.selectedIndex++
  }
}

const removeProperties = (propertyNames: string[]) => {
  const selected = new Set(propertyNames)

  includedProperties.value = includedProperties.value.filter((property) => !selected.has(property.defaultHeader))

  availableProperties.value.forEach((property) => {
    if (selected.has(property.defaultHeader)) {
      delete property.hidden
    }
  })

  if (includedPropertiesList.value) {
    // fix expected list selection behaviour (select last available item in the list when removing the last item)
    includedPropertiesList.value.selectedIndex = Math.min(includedPropertiesList.value.selectedIndex, includedProperties.value.length - 1)
  }
}

const orderIncludedProperties = (deltaChange: number) => {
  if (includedPropertiesList.value) {
    const firstPositionAfterChange = includedPropertiesList.value.selectedIndex + deltaChange
    const lastPositionAfterChange =
      (includedPropertiesList.value.selectedOptions.item(includedPropertiesList.value.selectedOptions.length - 1)?.index || 0) + deltaChange
    if (firstPositionAfterChange >= 0 && lastPositionAfterChange < includedProperties.value.length) {
      const selectedItems = Array.from(includedPropertiesList.value.selectedOptions)
      if (deltaChange > 0) {
        selectedItems.reverse()
      }
      selectedItems.forEach((selectedItem) => {
        moveArrayItem(includedProperties.value, selectedItem.index, deltaChange)
      })

      // fix expected list selection behaviour
      if (includedPropertiesList.value) {
        includedPropertiesList.value.selectedIndex = selectedItems[0].index + deltaChange // affects which element to start from when using keyboard navigation and automatically scrolls to the element
      }

      // when moving multiple items, make sure all items stay selected
      selectedItems.forEach((item) => {
        item.selected = false
        const option = includedPropertiesList.value?.item(item.index + deltaChange) as HTMLOptionElement | undefined

        if (option) {
          option.selected = true
        }
      })
    }
  }
}

const resetMappings = async () => {
  searchTerm.value = ''
  stagedLoadTemplate.value = undefined
  stagedSaveTemplate.value = ''
  availableProperties.value = await getProperties()
  includedProperties.value = []
}

const focusList = (list: HTMLSelectElement | undefined) => {
  list?.focus()
  if (list?.selectedIndex === -1 && list.length) {
    list.selectedIndex = 0
  }
}

const matchesSearchTerm = (name: string | undefined) => {
  return name?.toLowerCase().includes(searchTerm.value.toLowerCase())
}

const exportProducts = async (debugMode = false) => {
  exporting.value.productData = {
    total: props.total,
    link: route.fullPath,
  }

  try {
    await ExportService.exportProductData(route.query, output, includedProperties.value, fileName.value || defaultFileName.value, debugMode)
  } catch (err) {
    console.error(err)
  } finally {
    exporting.value.productData = {}
  }
}

const loadSelectedTemplate = async (loadTemplate: { id: string; visibility: TemplateVisibility } | undefined) => {
  if (loadTemplate) {
    await resetMappings()

    const template = store.templates.export[loadTemplate.visibility]?.find((template) => template.id === loadTemplate.id)

    if (template) {
      // const propertyNames = template.mappings.map((mapping) => PropertyService.getTranslatedHeader(mapping))
      store.useNumericValuesForExport = !!template.useNumericValues

      if (template.showSpecificCulturalFields !== undefined) {
        showSpecificCulturalFields.value = template.showSpecificCulturalFields
      }

      stagedSaveTemplate.value = template.name

      addProperties(template.mappings)
    } else {
      console.log('Could not find template')
    }
  }
}

const saveTemplate = async (templateName: string) => {
  const existing = store.templates.export[visibility.value]?.find((template) => areComparable(template.name, templateName))
  if (!existing || confirm(`Mallen '${templateName}' finns redan. Vill du skriva över?`)) {
    const template: ProductExportTemplateInput = {
      name: templateName,
      mappings: includedProperties.value.map((property) => ({
        id: property.id,
        culturePrefix: property.culturePrefix,
        defaultName: property.defaultName,
      })),
      useNumericValues: store.useNumericValuesForExport,
      showSpecificCulturalFields: showSpecificCulturalFields.value,
    }
    await DataService.saveTemplate('export', template, visibility.value, existing?.id)
  }
}

const removeSelectedTemplate = async () => {
  if (stagedLoadTemplate.value && confirm(`Är du säker på att du vill ta bort mallen '${stagedLoadTemplate.value.name}'?`)) {
    await DataService.removeTemplate('export', stagedLoadTemplate.value?.id, visibility.value)
    stagedLoadTemplate.value = undefined
  }
}

const exportEanLabels = async () => {
  if (!eanLabelCulture.value || !eanLabelPreview.value) {
    return
  }

  const total = props.total - eanLabelPreview.value.excluded.length

  exporting.value.eanLabels = {
    total,
    link: route.fullPath,
  }

  const date = new Date()
  const dateString = date.toISOString().slice(2, 10).replace(/-/g, '')

  try {
    await ExportService.exportEanLabels(
      new URLSearchParams(location.search),
      total,
      eanLabelType.value,
      `ean-labels-${dateString}`,
      eanLabelCulture.value,
      'pdf',
    )
  } finally {
    exporting.value.eanLabels = {}
  }
}

const abortProductsExport = () => {
  ExportService.abortProductsExport()
  exporting.value.productData = {}
}

const abortEanLabelExport = () => {
  ExportService.abortEanLabelExport()
  exporting.value.eanLabels = {}
}

const handleEscape = (event: KeyboardEvent) => {
  if (fileName.value.length) {
    event.stopPropagation()
  }
}

const handleSearchFromList = (event: KeyboardEvent) => {
  if (!event.ctrlKey && /^[a-zåäöøæ0-9\s-,.]$/i.test(event.key)) {
    event.preventDefault()
    searchTerm.value += event.key
  } else if (event.key === 'Backspace') {
    if (event.ctrlKey) {
      searchTerm.value = ''
    } else {
      searchTerm.value = searchTerm.value.slice(0, -1)
    }
    event.preventDefault()
    event.stopPropagation()
  } else if (event.key === 'Escape') {
    if (searchTerm.value) {
      searchTerm.value = ''
      event.preventDefault()
      event.stopPropagation()
    }
  }
}

const toggleEanLabelCulture = () => {
  eanLabelCultureLocked.value = !eanLabelCultureLocked.value

  if (eanLabelCultureLocked.value && store.dataCulture) {
    eanLabelCulture.value = store.dataCulture
  }
}

const triggerExportProductSheet = () => {
  emit('update:exportProductSheet', true)
}

const preventScrollOnFocus = async () => {
  const el: HTMLElement = modal.value?.$el.querySelector('.body')

  const scroll = el?.scrollTop
  window.requestAnimationFrame(() => {
    if (el && scroll !== undefined) {
      el.scrollTop = scroll
    }
  })
}

const loadPropertiesSelectedInTableView = async () => {
  const selectedCulturesTable = browserStorage.get<Culture[]>('productTable-selectedCultures')

  const selectedPropertiesTable = browserStorage.get('productTable-selectedProperties')
  const storedSet = new Set(
    Object.entries(selectedPropertiesTable)
      .filter(([, enabled]) => enabled)
      .map(([key]) => key),
  )

  includedProperties.value = []

  for (const property of availableProperties.value) {
    if (
      storedSet.has(property.key) &&
      (property.type === 'languageSpecific'
        ? selectedCulturesTable?.some((culture) => culture.language === property.culturePrefix)
        : property.type === 'countrySpecific'
          ? selectedCulturesTable?.some((culture) => culture.country === property.culturePrefix)
          : true)
    ) {
      includedProperties.value.push(property)
    }
  }
}
</script>

<style lang="scss" scoped>
.buttons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: right;

  button {
    width: 100%;
    margin-bottom: 0.5em;
  }
}

select.list {
  width: 100%;
  height: 50vh; //40em;

  &:focus {
    outline: blue;
  }

  option {
    padding: 0.25em 0.5em;

    &[disabled] {
      color: #ddd;
    }
  }
}

.vertically-faded {
  position: relative;

  &::after {
    content: '';
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background-image: linear-gradient(to bottom, transparent 60%, white);
  }
}

.list .disabled {
  color: #ddd !important; // different colour from the selected blurred row
}

.absolute-above {
  position: absolute;
  top: 0;
  left: 0;
  transform: translate(50%, -50%);

  width: unset;
  height: 0.75rem;
}
</style>
